import { cn } from "@/lib/utils";
import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from "react";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, ...props }, ref) => (
    <input
      type={type ?? "text"}
      ref={ref}
      className={cn(
        "flex h-10 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm shadow-sm transition-colors",
        "ring-offset-background placeholder:text-muted-foreground/75",
        "hover:border-foreground/25 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/10",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        "flex min-h-[96px] w-full rounded-lg border border-input bg-white px-3 py-2 text-sm shadow-sm transition-colors",
        "ring-offset-background placeholder:text-muted-foreground",
        "hover:border-foreground/25 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/10",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = "Textarea";

type LabelProps = React.LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean };

export const Label: React.FC<LabelProps> = ({ className, required, children, ...props }) => (
  <label className={cn("text-[13px] font-semibold leading-none text-foreground/85", className)} {...props}>
    {children}{required && <span aria-hidden="true" className="ml-1 text-destructive">*</span>}
  </label>
);

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className, children, ...props }) => (
  <select
    className={cn(
      "flex h-10 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm shadow-sm transition-colors",
      "hover:border-foreground/25 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/10",
      className,
    )}
    {...props}
  >
    {children}
  </select>
);

export const Checkbox: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className, ...props }) => (
  <input
    type="checkbox"
    className={cn(
      "h-4 w-4 rounded border border-input text-primary focus-visible:ring-2 focus-visible:ring-ring",
      className,
    )}
    {...props}
  />
);
